import type { MonetaryValue } from '@shared/lib/money/money'
import type { InvoiceStatus } from '@shared/contracts/enums'
import type { Subscription, SubscriptionInvoice } from './subscription.contract'

export interface SubscriptionChargeView {
  paidAmount?: MonetaryValue
  id: string
  displayCycleN: number
  dueAt: string
  status: InvoiceStatus
  chargeAmount?: MonetaryValue
  settlementAmount?: MonetaryValue
  value: MonetaryValue
  saleId: string
  isInitialPurchase: boolean
}

export function subscriptionChargeViewLabel(charge: SubscriptionChargeView): string {
  if (charge.isInitialPurchase) {
    return 'Primeira cobrança'
  }
  return `Renovação · ciclo ${charge.displayCycleN}`
}

function recurringFallback(subscription: Subscription): MonetaryValue | undefined {
  return subscription.chargeAmount ?? subscription.settlementAmount ?? subscription.recurringAmount
}

function invoicesIncludeInitialSale(
  subscription: Subscription,
  invoices: SubscriptionInvoice[],
): boolean {
  return invoices.some(
    (invoice) =>
      invoice.settlementSaleId === subscription.initialSaleId ||
      invoice.id === subscription.initialSaleId,
  )
}

export function buildSubscriptionChargeViews(
  subscription: Subscription,
  invoices: SubscriptionInvoice[],
): SubscriptionChargeView[] {
  const sorted = [...invoices].sort((left, right) => left.cycleN - right.cycleN)
  const includeInitial = !invoicesIncludeInitialSale(subscription, sorted)
  const fallback = recurringFallback(subscription)

  const invoiceViews = sorted.map((invoice) => ({
    ...(invoice.paidAmount !== undefined ? { paidAmount: invoice.paidAmount } : {}),
    id: invoice.id,
    displayCycleN: invoice.cycleN,
    dueAt: invoice.dueAt,
    status: invoice.status,
    ...(invoice.chargeAmount !== undefined ? { chargeAmount: invoice.chargeAmount } : {}),
    ...(invoice.settlementAmount !== undefined
      ? { settlementAmount: invoice.settlementAmount }
      : {}),
    value: invoice.value,
    saleId: invoice.settlementSaleId,
    isInitialPurchase:
      !includeInitial &&
      (invoice.settlementSaleId === subscription.initialSaleId ||
        invoice.id === subscription.initialSaleId),
  }))

  if (!includeInitial || fallback === undefined) {
    return invoiceViews
  }

  return [
    {
      id: `initial:${subscription.initialSaleId}`,
      displayCycleN: 1,
      dueAt: subscription.createdAt,
      status: 'PAID',
      ...(subscription.chargeAmount !== undefined
        ? { chargeAmount: subscription.chargeAmount }
        : fallback !== undefined
          ? { chargeAmount: fallback }
          : {}),
      ...(subscription.settlementAmount !== undefined
        ? { settlementAmount: subscription.settlementAmount }
        : {}),
      value: fallback,
      saleId: subscription.initialSaleId,
      isInitialPurchase: true,
    },
    ...invoiceViews,
  ]
}
